import { startRegistryProcess, navigateToProcessOrTask } from '@/api/backend/workflow';
import { PermitType, RegistryActionType, RegistryActionTypeCode } from '@/api/backend/types';
import { Container } from '@/components/container';
import { PermissionGuard } from '@/components/permission-guard';
import { RegistryStatusBadge } from '@/components/registry-status-badge';
import { SubNavContainer } from '@/components/sub-nav-container';
import { PROFILE_PERMISSION } from '@/lib/constants';
import { requirePermitAccess } from '@/lib/route-guards';
import { registryItemQueryOptions } from '@/queryOptions';
import { useBreadcrumbsStore } from '@/store/breadcrumbs';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from '@opaz/ui-components';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PermitDocumentsTab } from '../-components/PermitDocumentsTab';
import { PermitHistoryTab } from '../-components/PermitHistoryTab';
import { PermitsListTable } from '../-components/PermitsListTable';
import { RegistryAttributesListTable } from '../../-components/RegistryAttributesListTable';

export const Route = createFileRoute('/_auth/registry/permits/$permitId/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    version: search.version as string | undefined,
  }),
  context: ({ params }) => ({
    getBreadcrumbData: () => ({
      id: params.permitId,
      translationKey: null,
    }),
  }),
  beforeLoad: async ({ params, context, search }) => {
    requirePermitAccess(context);
    return context.queryClient.ensureQueryData(registryItemQueryOptions(params.permitId, search.version));
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Permit Details - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permitId } = useParams({ from: '/_auth/registry/permits/$permitId/' });
  const { version } = useSearch({ from: '/_auth/registry/permits/$permitId/' });
  const { data } = useSuspenseQuery(registryItemQueryOptions(permitId, version)) as { data: PermitType };
  const { updateIdToTextMap } = useBreadcrumbsStore((state) => state);

  // If the registry has no history, we show the base view (overview)
  const isRegistryBaseView = !data.history;

  const { mutateAsync: startProcess, isPending: isStartProcessLoading } = useMutation({
    mutationFn: async ({
      type,
      serviceCode,
      relatedRegistryId,
    }: {
      type: RegistryActionTypeCode;
      serviceCode: string;
      relatedRegistryId: string;
    }) => {
      const createdProcess = await startRegistryProcess({
        type,
        zoneCode: data.zoneCode,
        serviceCode,
        relatedRegistryId,
      });
      await navigateToProcessOrTask(createdProcess.id, navigate);
    },
  });

  const handleAction = (action: RegistryActionType) => {
    startProcess({
      type: action.type,
      serviceCode: action.serviceCode,
      relatedRegistryId: data.registryNumber,
    });
  };

  const handleReturnToLatest = () => {
    navigate({
      to: '/registry/permits/$permitId',
      params: { permitId },
      search: { version: undefined },
    });
  };

  useEffect(() => {
    updateIdToTextMap(permitId, data.registryNumber);
  }, [data.registryNumber, permitId, updateIdToTextMap]);

  return (
    <>
      <SubNavContainer>
        <div className="inline-flex flex-col gap-5">
          <div>
            <RegistryStatusBadge statusCode={data.statusCode} />
          </div>

          <h1 className="text-foreground text-3xl font-bold">
            {t([`backend.codes.${data.registryCode}` as unknown as TemplateStringsArray, 'common.unknown'])}
          </h1>
        </div>
      </SubNavContainer>

      <Container>
        {isRegistryBaseView && (
          <Alert variant="default" className="mb-4">
            <AlertTitle>{t('registry.permit.baseViewBanner.title')}</AlertTitle>
            <AlertDescription>{t('registry.permit.baseViewBanner.description')}</AlertDescription>
          </Alert>
        )}

        {version && (
          <Alert className="mb-6" variant="warning">
            <AlertTitle>{t('registry.permit.historicalVersion.title', 'Historical Version')}</AlertTitle>
            <AlertDescription>
              <span>
                {t(
                  'registry.permit.historicalVersion.description',
                  'You are viewing a historical version of this permit.',
                  { version },
                )}
              </span>
              <div className="mt-4 flex justify-start">
                <Button variant="secondary" onClick={handleReturnToLatest}>
                  {t('registry.permit.historicalVersion.returnToLatest', 'Return to Latest Version')}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-foreground text-2xl font-bold">{t('registry.permit.sectionDetails.title')}</h2>

          <PermissionGuard permission={PROFILE_PERMISSION.PMS_PERMIT}>
            <div className="flex gap-2">
              {data.actions
                ?.filter((action) => !(action.type === 'ACT_CANCEL' && data.statusCode === 'CANCELED'))
                .map((action) => (
                  <Button
                    key={action.type}
                    variant={action.type === 'ACT_CANCEL' ? 'destructive' : 'secondary'}
                    onClick={() => handleAction(action)}
                    disabled={isStartProcessLoading}
                  >
                    {t(`registry.permit.sectionDetails.actionButtons.${action.type}`)}
                  </Button>
                ))}
            </div>
          </PermissionGuard>
        </div>

        <PermitsListTable className="mb-12" data={data} />

        {!isRegistryBaseView && (
          <>
            <RegistryAttributesListTable
              title={t('registry.permit.sectionAttributes.title')}
              attributes={data.attributes}
            />

            {/* Tabs for History and Documents */}
            <TabsRoot defaultValue="history" className="mt-8">
              <TabsList className="mb-6">
                <TabsTrigger value="history">{t('registry.permit.sectionTabs.historyTab.title')}</TabsTrigger>
                <TabsTrigger value="documents">{t('registry.permit.sectionTabs.documentsTab.title')}</TabsTrigger>
              </TabsList>

              <TabsContent value="history">
                <PermitHistoryTab permitId={permitId} history={data.history} />
              </TabsContent>

              <TabsContent value="documents">
                <PermitDocumentsTab data={data} />
              </TabsContent>
            </TabsRoot>
          </>
        )}
      </Container>
    </>
  );
}
